import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  DrawerCloseButton,
  DrawerHeader,
  Divider,
  Spinner,
  Center,
  Text,
  Flex,
  Box,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Tabs,
  Button,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/react'
import { isUndefined } from 'lodash'
import moment from 'moment'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdOutlineShare } from 'react-icons/md'
import useFetch, { CachePolicies } from 'use-http'
import { checkOperationPermission } from '../../../../utils/permissions'
import { ACLPermissionEnum } from '../../../constants'
import useToast from '../../../hooks/useToast'
import type {
  FolderDetailsResponse,
  MediaLibrarySearchFolderResponse,
} from '../../../interfaces'
import { UserContext } from '../../../providers'
import CustomMetadataForm from '../Details/CustomMetadataForm'
import ShareAssetModal from './AccessControl/ShareAsset'
import AuditLogs from '../Details/AuditLogs'

const SummaryTabPanel = ({
  folder,
  folderDetails,
  onCloseParent,
}: {
  folder: MediaLibrarySearchFolderResponse
  folderDetails: FolderDetailsResponse
  onCloseParent(): void
}): React.ReactElement => {
  const { user } = useContext(UserContext)
  const { t } = useTranslation()
  const {
    isOpen: isOpenShare,
    onOpen: onOpenShare,
    onClose: onCloseShare,
  } = useDisclosure()

  const canManage = useMemo(
    () =>
      checkOperationPermission({
        user,
        aclPermission: [ACLPermissionEnum.MANAGE],
        selectedFiles: [],
        selectedFolders: [folder],
        selectedMediaCollections: [],
      }),
    [folder],
  )

  return (
    <>
      {isUndefined(folderDetails.createdAt) ? null : (
        <Box>
          <Flex direction="column">
            <Flex marginBottom="10px">
              <Text marginRight="5px">{t('created', 'Created')} :</Text>
              <Text as="b">
                {moment(folderDetails.createdAt).format('MMMM Do YYYY')}
              </Text>
            </Flex>
          </Flex>
        </Box>
      )}
      {isUndefined(folderDetails.updatedAt) ? null : (
        <Box>
          <Flex direction="column">
            <Flex marginBottom="10px">
              <Text marginRight="5px">
                {t('last_modified', 'Last modified')} :
              </Text>
              <Text as="b">
                {moment(folderDetails.updatedAt).format('MMMM Do YYYY')}
              </Text>
            </Flex>
          </Flex>
        </Box>
      )}
      {isUndefined(folderDetails.createdBy) ? null : (
        <Box>
          <Flex direction="column">
            <Flex marginBottom="10px">
              <Text marginRight="5px">{t('creator', 'Creator')} :</Text>
              <Tooltip label={folderDetails.createdBy.email}>
                <Text as="b">{folderDetails.createdBy.name}</Text>
              </Tooltip>
            </Flex>
          </Flex>
        </Box>
      )}
      {!canManage ? null : (
        <Box>
          <Button leftIcon={<MdOutlineShare />} onClick={onOpenShare}>
            {t('share', 'Share')}
          </Button>
          <ShareAssetModal
            isOpen={isOpenShare}
            onClose={() => {
              onCloseParent()
              onCloseShare()
            }}
            assetId={folder.folderId}
            assetName={folderDetails.name}
            assetType="folder"
          />
        </Box>
      )}
    </>
  )
}

interface FolderDetailsDrawerProps {
  clientNumber?: string
  folder?: MediaLibrarySearchFolderResponse
  isOpen: boolean
  onClose(): void
}

const FolderDetailsDrawer = ({
  clientNumber,
  folder,
  isOpen,
  onClose,
}: FolderDetailsDrawerProps): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const folderId = folder?.folderId

  const { get, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [folderDetails, updateFolderDetails] = useState<
    FolderDetailsResponse | undefined
  >()

  const [drawerTab, updateDrawerTab] = useState<'summary' | 'cm'>('summary')

  useEffect(() => {
    if (!folderId || !clientNumber || !isOpen) {
      return
    }
    get(`/api/v2/folder/details?folderId=${folderId}`)
      .then(res => {
        updateFolderDetails({
          ...res,
          folderId,
        })
      })
      .catch(() => {
        toast({
          description: 'Something went wrong!',
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      })
  }, [folderId, clientNumber, isOpen])

  const CmForm = useMemo(
    () => (): React.ReactElement =>
      folder ? (
        <CustomMetadataForm folder={folder} folderDetails={folderDetails} />
      ) : (
        <></>
      ),
    [folderDetails, drawerTab],
  )

  if (!folderId) {
    return <></>
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="sm">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          {loading || isUndefined(folderDetails) ? '' : folderDetails.name}
        </DrawerHeader>
        <Divider />
        {loading || isUndefined(folder) || isUndefined(folderDetails) ? (
          <Center width="full" height="100%">
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="appPaleGrey"
              color="appBlue"
              size="xl"
            />
          </Center>
        ) : (
          <DrawerBody padding={0} height="100%">
            <Tabs
              display="flex"
              flexDirection="column"
              minHeight="100%"
              height="100%"
              size="xl"
              overflow="hidden"
            >
              <TabList display="flex" justifyContent="space-between">
                <Tab>{t('summary', 'Summary')}</Tab>
                <Tab
                  onClick={() => {
                    // Trigger re-render of <CmForm /> to wrap labels with ellipsis
                    updateDrawerTab('cm')
                  }}
                >
                  {t('custom_metadata', 'Custom metadata')}
                </Tab>
                <Tab>{t('history', 'History')}</Tab>
              </TabList>
              <TabPanels minHeight="0" display="flex" flexGrow={1}>
                <TabPanel>
                  <SummaryTabPanel
                    folder={folder}
                    folderDetails={folderDetails}
                    onCloseParent={onClose}
                  />
                </TabPanel>
                <TabPanel
                  minHeight="0"
                  display="flex"
                  flexGrow={1}
                  maxWidth="45vw"
                >
                  <CmForm key="cm" />
                </TabPanel>
                <TabPanel flexGrow={1} overflowY="auto">
                  <AuditLogs
                    entityId={folderId}
                    entityDetails={folderDetails}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>
        )}
      </DrawerContent>
    </Drawer>
  )
}

export default FolderDetailsDrawer
